import { Fragment, useMemo, useState } from "react";
import styled from "styled-components";
import {Icon} from '@blueprint/ui';

import { Badge, Table } from "../../../common/components/core";
import {
    ApproveStatus,
    BesuClaimApprovers,
    ApproverDetailsPersonalInfo,
} from "../../../types/claim";
import { CommentRecord } from "../../../types/claim";
import { formatToDateWithTime } from "../../../utils/common";

type ClaimApproversTableProps = {
    data: BesuClaimApprovers[];
    comments?: CommentRecord[];
};

type ClaimApproverRow = {
    data: ApproverDetailsPersonalInfo;
    expand: boolean;
    comment?: string;
};

const generateApproverStatusBadge = (status?: string) => {
    switch (status) {
        case ApproveStatus.APPROVED:
            return <Badge type="success">Approved</Badge>;
        case ApproveStatus.REJECTED:
            return <Badge type="danger">Rejected</Badge>;
        case ApproveStatus.INVALID:
            return <Badge type="discarded">Invalid</Badge>;
        default:
            return <Badge type="info">Pending</Badge>;
    }
};

export const ClaimApproversTable = ({
    data,
    comments,
}: ClaimApproversTableProps): JSX.Element => {
    const initClaimApproverRows: ClaimApproverRow[] = useMemo(() => {
        const newClaimApproverRows: ClaimApproverRow[] = data.map((row) => {
            return {
                data: row.details,
                comment:
                    comments &&
                    comments.find(
                        (comment) => comment.email === row.details.email,
                    )?.commentRef,
                expand: false,
            };
        });
        return newClaimApproverRows;
    }, [data, comments]);

    const [claimApproverRows, setClaimApproverRows] = useState<
        ClaimApproverRow[]
    >(initClaimApproverRows);
    const handleExpandRow = (index: number) => {
        const newExpandStateRows = claimApproverRows.map((row, i) =>
            i === index ? { ...row, expand: !row.expand } : row,
        );
        setClaimApproverRows(newExpandStateRows);
    };
    return (
        <>
            <table className={'custom-table'}>
                <thead>
                    <tr>
                        <Table.HeaderCell width={180}>Name</Table.HeaderCell>
                        <Table.HeaderCell width={280}>
                            Email Address
                        </Table.HeaderCell>
                        <Table.HeaderCell width={120}>
                            Role Name
                        </Table.HeaderCell>
                        <Table.HeaderCell width={180}>
                            Timestamp
                        </Table.HeaderCell>
                        <Table.HeaderCell width={148}>Status</Table.HeaderCell>
                        <Table.HeaderCell width={0}>Comment</Table.HeaderCell>
                        <Table.HeaderCell width={24}>Expand</Table.HeaderCell>
                    </tr>
                </thead>
                <tbody>
                    {data.map((data, index) => {
                        const isExpand = claimApproverRows[index]?.expand;
                        return (
                            <Fragment key={index}>
                                <StyledTableRowWithComment isExpand={isExpand}>
                                    <StyledTd>
                                        {" "}
                                        {`${data.details.firstName} ${data.details.lastName}`}
                                    </StyledTd>
                                    <StyledTd>{data.details.email}</StyledTd>
                                    <StyledTd>{data.details.roleName}</StyledTd>
                                    <StyledTd>
                                        {/* TODO: Hot fix for project missing timestamp, should move to claim state */}
                                        {formatToDateWithTime(
                                            data?.actionTimestamp ??
                                                new Date(
                                                    Date.now(),
                                                ).toISOString(),
                                        )}
                                    </StyledTd>
                                    <StyledTd>
                                        {generateApproverStatusBadge(
                                            data.status,
                                        )}
                                    </StyledTd>
                                    {isExpand ? (
                                        <StyledTd>
                                            {" "}
                                            Comment:{" "}
                                            {claimApproverRows[index].comment &&
                                                claimApproverRows[index]
                                                    .comment}
                                        </StyledTd>
                                    ) : (
                                        <td></td>
                                    )}
                                    <StyledTd>
                                        {data.status ===
                                            ApproveStatus.REJECTED && (
                                            <>
                                                {isExpand ? (
                                                    <Icon
                                                        onClick={() =>
                                                            handleExpandRow(
                                                                index,
                                                            )
                                                        }
                                                        onKeyUp={(event:KeyboardEvent) =>
                                                            {(event.keyCode === 13 || event.keyCode === 32) && handleExpandRow(index)}
                                                        }
                                                        titleAccess={'ChevronUp'}
                                                        name={'ChevronUp'}
                                                        tabIndex={0}
                                                    />
                                                ) : (
                                                    <Icon
                                                        onClick={() =>
                                                            handleExpandRow(
                                                                index,
                                                            )
                                                        }
                                                        onKeyUp={(event:KeyboardEvent) =>
                                                            {(event.keyCode === 13 || event.keyCode === 32) && handleExpandRow(index)}
                                                        }
                                                        titleAccess={'ChevronDown'}
                                                        name={'ChevronDown'}
                                                        tabIndex={0}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </StyledTd>
                                </StyledTableRowWithComment>
                            </Fragment>
                        );
                    })}
                </tbody>
            </table>
        </>
    );
};

const StyledTd = styled.td`
    vertical-align: text-top;
`;

const StyledTableRow = styled(Table.BodyRow)`
    cursor: initial;
    :hover {
        background-color: #f9f9f9;
    }
`;

const StyledTableRowWithComment = styled(StyledTableRow)<{ isExpand: boolean }>`
    position: relative;
    height: ${(props) => (props.isExpand ? "100px !important" : "")};
    td {
        vertical-align: ${(props) => (props.isExpand ? "text-top !important" : "")};
    }
    
    
`;
